import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SlideOver, SlideOverContent } from '@/components/ui/slide-over';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoaderIcon, PencilIcon } from '@/components/icons';
import { cortexApi } from '@/api/cortex.api';
import type { Skill, SkillGroup } from '@/proto/agentplatform/v1/skill_pb';
import { SkillGroupSchema } from '@/proto/agentplatform/v1/skill_pb';
import { formatTimestamp } from '@/utils/utils';

type SkillVersionMap = Record<string, number>;
type SkillVersionOption = {
  value: number;
  label: string;
  isLatest: boolean;
  isStable: boolean;
};

type GroupFormState = {
  name: string;
  description: string;
  classParamsText: string;
};

function parseStringMapJson(raw: string): Record<string, string> {
  const parsed = JSON.parse(raw.trim() || '{}');
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('classParams 必须是 JSON 对象');
  }
  return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, String(v)]));
}

export default function SkillGroupDetailPage() {
  const navigate = useNavigate();
  const { uid: id = '' } = useParams<{ uid: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['skillgroup-detail', id],
    enabled: !!id,
    queryFn: () => cortexApi.getSkillGroup(id),
  });
  const skillIDs = Object.keys(data?.skills || {});
  const { data: skillsData } = useQuery({
    queryKey: ['skills-options-for-group-detail', id, skillIDs.slice().sort()],
    enabled: !!id && skillIDs.length > 0,
    queryFn: () =>
      cortexApi.listSkills({
        page: 1,
        pageSize: 100,
        keyword: '',
        withVersions: false,
      }),
  });

  // ---- edit form state ----
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<GroupFormState>({
    name: '',
    description: '',
    classParamsText: '{}',
  });
  const [selectedSkillsMap, setSelectedSkillsMap] = useState<SkillVersionMap>({});
  const [leftChecked, setLeftChecked] = useState<string[]>([]);
  const [rightChecked, setRightChecked] = useState<string[]>([]);
  const [skillKeyword, setSkillKeyword] = useState('');
  const [formError, setFormError] = useState('');

  const {
    data: allSkillsData,
    isLoading: allSkillsLoading,
    refetch: refetchAllSkills,
  } = useQuery({
    queryKey: ['skills-options-for-skillgroup-detail-edit'],
    enabled: showForm,
    queryFn: () =>
      cortexApi.listSkills({
        page: 1,
        pageSize: 100,
        keyword: '',
        withVersions: false,
      }),
  });

  const allSkills = useMemo(() => allSkillsData?.data || [], [allSkillsData]);
  const skillMap = useMemo(() => new Map(allSkills.map((item) => [item.name, item])), [allSkills]);
  const skillIdMap = useMemo(() => new Map(allSkills.map((item) => [item.id, item])), [allSkills]);

  const selectedSkillNames = useMemo(() => Object.keys(selectedSkillsMap), [selectedSkillsMap]);

  const selectedSkills = useMemo(
    () =>
      selectedSkillNames.map((key) => {
        const matched = skillMap.get(key) || skillIdMap.get(key);
        if (matched) return matched;
        return { id: key, name: key, description: '' } as Skill;
      }),
    [selectedSkillNames, skillMap, skillIdMap]
  );

  const availableSkills = useMemo(() => {
    const selectedKeySet = new Set(selectedSkillNames);
    const kw = skillKeyword.trim().toLowerCase();
    return allSkills.filter((item) => {
      if (selectedKeySet.has(item.name) || selectedKeySet.has(item.id)) return false;
      if (!kw) return true;
      return (
        item.name.toLowerCase().includes(kw) || (item.description || '').toLowerCase().includes(kw)
      );
    });
  }, [selectedSkillNames, skillKeyword, allSkills]);

  const { data: skillVersionOptionsMap = {} } = useQuery({
    queryKey: ['skill-version-options-detail', selectedSkillNames],
    enabled: showForm && selectedSkillNames.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        selectedSkillNames.map(async (skillName) => {
          const skill = skillMap.get(skillName);
          if (!skill?.id) return [skillName, []] as const;
          try {
            const resp = await cortexApi.listSkillVersions(skill.id);
            const options: SkillVersionOption[] = (resp.versions || [])
              .map((version) => {
                const code = Number(version.versionCode);
                const tags = [
                  version.isLatest ? 'latest' : '',
                  version.isStable ? 'stable' : '',
                ].filter(Boolean);
                const suffix = tags.length > 0 ? ` (${tags.join(', ')})` : '';
                return {
                  value: code,
                  label: `v${code}${suffix}`,
                  isLatest: !!version.isLatest,
                  isStable: !!version.isStable,
                };
              })
              .sort((a, b) => b.value - a.value);
            return [skillName, options] as const;
          } catch {
            return [skillName, []] as const;
          }
        })
      );
      return Object.fromEntries(entries) as Record<string, SkillVersionOption[]>;
    },
  });

  const getSkillVersionOptions = useCallback(
    (skillName: string, selectedVersion?: number): SkillVersionOption[] => {
      const base = skillVersionOptionsMap[skillName] || [];
      if (selectedVersion === undefined || base.some((item) => item.value === selectedVersion)) {
        return base;
      }
      const label = selectedVersion === 0 ? 'latest' : `v${selectedVersion}`;
      return [
        ...base,
        { value: selectedVersion, label, isLatest: selectedVersion === 0, isStable: false },
      ].sort((a, b) => b.value - a.value);
    },
    [skillVersionOptionsMap]
  );

  const getDefaultSkillVersion = useCallback(
    (skillName: string): number => {
      const options = getSkillVersionOptions(skillName);
      return options.find((item) => item.isLatest)?.value ?? options[0]?.value ?? 0;
    },
    [getSkillVersionOptions]
  );

  // 编辑时将 skills map 的 key 从 skill ID 转换为 skill name（与列表页编辑逻辑一致）
  useEffect(() => {
    if (!showForm) return;
    setSelectedSkillsMap((prev) => {
      let changed = false;
      const next: SkillVersionMap = {};
      for (const [key, version] of Object.entries(prev)) {
        const skillById = skillIdMap.get(key);
        if (skillById) {
          next[skillById.name] = version;
          changed = true;
        } else {
          next[key] = version;
        }
      }
      return changed ? next : prev;
    });
  }, [showForm, skillIdMap]);

  // 新增技能默认版本为 0（latest），待版本选项加载后自动替换为实际最新版本号
  useEffect(() => {
    if (!showForm) return;
    setSelectedSkillsMap((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const skillName of selectedSkillNames) {
        if (!(skillName in next)) continue;
        if (next[skillName] === 0) {
          const preferred = getDefaultSkillVersion(skillName);
          if (preferred !== 0) {
            next[skillName] = preferred;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [showForm, selectedSkillNames, skillVersionOptionsMap, getDefaultSkillVersion]);

  const openEditForm = () => {
    if (!data) return;
    setForm({
      name: data.name,
      description: data.description,
      classParamsText: JSON.stringify(data.classParams || {}, null, 2),
    });
    setSelectedSkillsMap(
      Object.fromEntries(
        Object.entries(data.skills || {}).map(([name, version]) => [name, Number(version) || 0])
      )
    );
    setLeftChecked([]);
    setRightChecked([]);
    setSkillKeyword('');
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ name: '', description: '', classParamsText: '{}' });
    setSelectedSkillsMap({});
    setLeftChecked([]);
    setRightChecked([]);
    setSkillKeyword('');
    setFormError('');
  };

  const updateMutation = useMutation({
    mutationFn: ({ id: groupId, payload }: { id: string; payload: SkillGroup }) =>
      cortexApi.updateSkillGroup(groupId, payload),
    onSuccess: () => {
      closeForm();
      queryClient.invalidateQueries({ queryKey: ['skillgroup-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['skillgroups'] });
    },
  });

  const normalizeSkillVersionMapToIDs = (source: SkillVersionMap): SkillVersionMap => {
    return Object.fromEntries(
      Object.entries(source).map(([skillKey, version]) => {
        const normalizedID = skillMap.get(skillKey)?.id || skillKey;
        return [normalizedID, version];
      })
    );
  };

  const submitForm = () => {
    try {
      setFormError('');
      const skillsByID = normalizeSkillVersionMapToIDs(selectedSkillsMap);
      const payload = create(SkillGroupSchema, {
        name: form.name.trim(),
        description: form.description.trim(),
        classId: data?.classId || '',
        provider: data?.provider || 'default',
        classParams: parseStringMapJson(form.classParamsText),
        skills: skillsByID,
      });
      updateMutation.mutate({ id: id!, payload });
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '表单数据不合法');
    }
  };

  // ---- skill selection helpers ----
  const toggleLeftChecked = (name: string) => {
    setLeftChecked((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };
  const toggleRightChecked = (name: string) => {
    setRightChecked((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };
  const addSelectedSkills = () => {
    if (leftChecked.length === 0) return;
    setSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const name of leftChecked) {
        if (!next[name]) next[name] = getDefaultSkillVersion(name);
      }
      return next;
    });
    setLeftChecked([]);
  };
  const addAllVisibleSkills = () => {
    if (availableSkills.length === 0) return;
    setSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const item of availableSkills) {
        if (!next[item.name]) next[item.name] = getDefaultSkillVersion(item.name);
      }
      return next;
    });
    setLeftChecked([]);
  };
  const removeSelectedSkills = () => {
    if (rightChecked.length === 0) return;
    const removed = new Set(rightChecked);
    setSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const name of removed) delete next[name];
      return next;
    });
    setRightChecked([]);
  };
  const removeAllSkills = () => {
    setSelectedSkillsMap({});
    setRightChecked([]);
  };
  const setSkillVersion = (name: string, version: number) => {
    setSelectedSkillsMap((prev) => ({ ...prev, [name]: version }));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <p className="text-sm text-destructive">技能组加载失败</p>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={() => navigate('/plugins/skills?tab=group')}>
                返回技能组
              </Button>
              <Button onClick={() => refetch()}>重试</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const skills = Object.entries(data.skills || {});
  const classParams = Object.entries(data.classParams || {});
  const skillNameMap = new Map((skillsData?.data || []).map((item) => [item.id, item.name]));
  const skillIDByNameMap = new Map((skillsData?.data || []).map((item) => [item.name, item.id]));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{data.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">技能组详情信息。</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/plugins/skills?tab=group')}>
            返回技能组
          </Button>
          {data.classId && (
            <Button
              variant="outline"
              onClick={() => navigate(`/plugins/skills/group-classes/${data.classId}`)}
            >
              查看技能组模版
            </Button>
          )}
          <Button onClick={openEditForm}>
            <PencilIcon className="h-4 w-4 mr-1" />
            编辑
          </Button>
          <Button variant="outline" onClick={() => refetch()}>
            刷新
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本信息</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground mb-1">ID</div>
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{data.id}</code>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">描述</div>
            <div>{data.description || '-'}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">技能组模版 ID</div>
            {data.classId ? (
              <button
                type="button"
                className="text-primary hover:text-primary/80 font-mono text-xs"
                onClick={() => navigate(`/plugins/skills/group-classes/${data.classId}`)}
              >
                {data.classId}
              </button>
            ) : (
              '-'
            )}
          </div>
          <div>
            <div className="text-muted-foreground mb-1">创建时间</div>
            <div>{formatTimestamp(data.createdAt)}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">更新时间</div>
            <div>{formatTimestamp(data.updatedAt)}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">技能列表</CardTitle>
        </CardHeader>
        <CardContent>
          {skills.length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无技能配置。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>技能名</TableHead>
                  <TableHead>版本</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skills.map(([skillID, version]) => (
                  <TableRow key={skillID}>
                    <TableCell className="font-medium">
                      {(() => {
                        const resolvedSkillID = skillNameMap.has(skillID)
                          ? skillID
                          : (skillIDByNameMap.get(skillID) ?? '');
                        const displayName = skillNameMap.get(resolvedSkillID || skillID) || skillID;
                        if (!resolvedSkillID) return displayName;
                        return (
                          <button
                            type="button"
                            className="text-primary hover:text-primary/80 text-left"
                            onClick={() =>
                              navigate(`/plugins/skills/management/${resolvedSkillID}`)
                            }
                          >
                            {displayName}
                          </button>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{version || 'latest'}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">模版参数</CardTitle>
        </CardHeader>
        <CardContent>
          {classParams.length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无模版参数覆盖。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>参数名</TableHead>
                  <TableHead>参数值</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classParams.map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell className="font-mono text-xs">{key}</TableCell>
                    <TableCell className="font-mono text-xs">{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit SlideOver */}
      <SlideOver
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
        title="编辑技能组"
        actions={
          <Button
            onClick={submitForm}
            disabled={
              !form.name.trim() || selectedSkillNames.length === 0 || updateMutation.isPending
            }
          >
            {updateMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
            保存
          </Button>
        }
      >
        <SlideOverContent>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">名称 *</p>
            <Input
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">描述</p>
            <textarea
              className="w-full min-h-20 rounded-md border px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Class Params (JSON)</p>
            <textarea
              className="w-full min-h-24 rounded-md border px-3 py-2 text-xs font-mono"
              value={form.classParamsText}
              onChange={(e) => setForm((s) => ({ ...s, classParamsText: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">技能 *</p>
              <Button size="sm" variant="outline" onClick={() => refetchAllSkills()}>
                刷新技能列表
              </Button>
            </div>
            <Input
              placeholder="搜索可选技能"
              value={skillKeyword}
              onChange={(e) => setSkillKeyword(e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-start">
              <div className="rounded-md border bg-background">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  可选技能（{availableSkills.length}）
                </div>
                <div className="max-h-56 overflow-auto p-1">
                  {allSkillsLoading ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">加载中...</p>
                  ) : availableSkills.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">暂无可选技能</p>
                  ) : (
                    availableSkills.map((item) => (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => toggleLeftChecked(item.name)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs ${
                          leftChecked.includes(item.name)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="font-medium break-all">{item.name}</div>
                        <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden">
                          {item.description || '-'}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="flex md:flex-col gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={addSelectedSkills}>
                  &gt;
                </Button>
                <Button size="sm" variant="outline" onClick={addAllVisibleSkills}>
                  &gt;&gt;
                </Button>
                <Button size="sm" variant="outline" onClick={removeSelectedSkills}>
                  &lt;
                </Button>
                <Button size="sm" variant="outline" onClick={removeAllSkills}>
                  &lt;&lt;
                </Button>
              </div>
              <div className="rounded-md border bg-background">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  已选技能（{selectedSkills.length}）
                </div>
                <div className="max-h-56 overflow-auto p-1">
                  {selectedSkills.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">未选择技能</p>
                  ) : (
                    selectedSkills.map((item) => (
                      <div
                        key={item.name}
                        className={`px-2 py-1.5 rounded text-xs ${
                          rightChecked.includes(item.name)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleRightChecked(item.name)}
                          className="w-full text-left"
                        >
                          <div className="font-medium break-all">{item.name}</div>
                          <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden">
                            {item.description || '-'}
                          </div>
                        </button>
                        <div className="mt-1">
                          <select
                            value={
                              selectedSkillsMap[item.name] ?? getDefaultSkillVersion(item.name)
                            }
                            onChange={(e) => setSkillVersion(item.name, Number(e.target.value))}
                            className="h-7 rounded border border-input bg-background px-2 text-xs text-foreground"
                          >
                            {getSkillVersionOptions(item.name, selectedSkillsMap[item.name]).map(
                              (option) => (
                                <option
                                  key={`${item.name}-detail-${option.value}`}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          {formError && <p className="text-xs text-destructive">{formError}</p>}
        </SlideOverContent>
      </SlideOver>
    </div>
  );
}
